import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import i18n from '../../utils/i18n';

import { setParams } from '../../utils/home/filters';
import trackBeforeRedirect from '../../utils/home/trackBeforeRedirect';

import { SUPPORTED_TARGETS } from '../../utils/home/constants';

import { showLocationSearchModal } from '../../actions/locationActions';
import {
  buildSelectedFilters,
  getSubtitle,
  getCollectionLink,
  getTitle,
  getSearchId,
  buildHomeFeedUrl,
} from '../../selectors/homeSelectors';
import { maybeSetClientCookie } from '../../utils/cookies/client';
import isRedirectToHomeActive from '../../utils/home/isRedirectToHomeActive';
import { getTargetURL } from '../../utils/home/factories';
import getSafe from '../../utils/getSafe';
import { HOME_FEED_REFERRER, HOMEPAGE_REFERRER } from '../../utils/constants';

import { requestHomeFeed, applyActionTarget } from '../../actions/homeFeedActions';
import { trackTappedFilterOption } from '../../actions/tracking/filters';
import {
  trackViewedHome,
  trackViewedCollection,
  trackTappedLayoutBlock,
  trackTappedLayout,
} from '../../actions/tracking/home';

import HomeFeed from '../../components/HomeFeed';
import HomeSummary from '../../components/HomeSummary';

import styles from './styles.scss';

// eslint-disable-next-line react/no-deprecated
export class HomeFeedResults extends Component {
  constructor(props) {
    super();

    this.state = {
      selectedFilters: props.selectedFilters,
      isErrorPage: false,
    };
  }

  componentWillReceiveProps(nextProps) {
    const { isErrorPage } = this.state;
    // If the searchIds are different this indicates a successful response to the API with a new results set.
    const isNewResults = nextProps.searchId !== this.props.searchId;
    /*
      Check if we have a response and the loading state has stopped.
      Check if the searchId's from the response is different to the previous searchId, or check if there was an error.
      Check if the Error page is visible.
    */
    if (!nextProps.loading && (isNewResults || nextProps.error) && !isErrorPage) {
      // If there is an error set isErrorPage to true to avoid the above condition continuously passing.

      this.setSelectedFiltersFromResponse({
        selectedFilters: nextProps.selectedFilters,
        isErrorPage: !!nextProps.error,
        isCollectionView: nextProps.titleLink,
      });
    }

    if (isNewResults && isErrorPage) {
      // If there is a successful response while the error page is visible, update the selectedFilters and set isErrorPage to false.
      this.setSelectedFiltersFromResponse({
        selectedFilters: nextProps.selectedFilters,
        isErrorPage: false,
        isCollectionView: nextProps.titleLink,
      });
    }
  }

  componentDidMount() {
    /*
      The titleLink property only exists when we are in a collection view. We don't want to send the Viewed Home event when in a collection view. We will send a Viewed Collection event instead.
    */
    if (!this.props.titleLink) {
      const extraTrackingProps = {};
      if (this.props.isRedirectToHomeActive) {
        const lastHomeFeedReferrer = getSafe(() => JSON.parse(this.props.lastHomeFeed).referrer);
        extraTrackingProps.hasBeenRedirected = lastHomeFeedReferrer === HOMEPAGE_REFERRER;

        const value = JSON.stringify({
          url: this.props.homeFeedUrl,
          referrer: HOME_FEED_REFERRER,
        });
        maybeSetClientCookie('cwa_last_home_feed', value, { expires: 30 });
      }
      this.props.trackViewedHome(extraTrackingProps);
    } else {
      this.props.trackViewedCollection();
    }
  }

  handleEditLocation = (e) => {
    e.preventDefault();
    this.props.showLocationSearchModal();
  };

  setSelectedFiltersFromResponse = ({ selectedFilters, isErrorPage, isCollectionView }) => {
    this.setState({ selectedFilters, isErrorPage });
    /*
        The titleLink property only exists when we are in a collection view. We don't want to send the Viewed Home event when in a collection view. We will send a Viewed Collection event instead.
      */
    if (isCollectionView) {
      this.props.trackViewedCollection();
    } else {
      this.props.trackViewedHome();
    }
  };

  setSelectedFilters = (filterOption, filter) => {
    this.props.trackTappedFilterOption({ filterOption, filter, selected: !filterOption.selected });

    const { selectedFilters } = this.state;
    this.setState(
      { selectedFilters: setParams({ type: filter.optionsType, filterOption, selectedFilters }) },
      () => {
        this.props.applyFilters(this.state.selectedFilters);
      },
    );
  };

  handleBannerClick = ({ target, event: e, layoutTrackingId, blockTrackingId }) => {
    const track = () => {
      this.props.trackTappedLayoutBlock({
        layoutTrackingId,
        blockTrackingId,
      });
    };
    if (target.typeName === SUPPORTED_TARGETS.ACTION) {
      this.props.applyActionTarget(target.action);
      track();
    } else if (target.typeName === SUPPORTED_TARGETS.PARAMS) {
      this.props.onGetCollection([target.queryParams]);
      track();
    } else if (
      [SUPPORTED_TARGETS.RESTAURANT, SUPPORTED_TARGETS.MENU_ITEM].includes(target.typeName)
    ) {
      const url = getTargetURL(target);
      if (url) {
        trackBeforeRedirect(e, url, track);
      }
    }
  };

  handleGetCollection = (params) => {
    this.props.onGetCollection([params]);
  };

  handleExitCollection = () => {
    this.props.onExitCollection();
  };

  handleClearFilter = (target) => {
    const { selectedFilters } = this.state;

    const params = selectedFilters.filter((p) => !p.includes(target.queryParams));
    this.props.clearFilter(params);
  };

  render() {
    const { results, subtitle, loading, title, titleLink, appliedFilters } = this.props;

    return (
      <div className={styles.container}>
        <HomeSummary
          title={title}
          titleLink={titleLink}
          subtitle={subtitle}
          isPlaceholder={loading}
          onClickBackToRestaurants={this.handleExitCollection}
          appliedFilters={appliedFilters}
          handleClearFilter={this.handleClearFilter}
        />
        <HomeFeed
          results={results}
          handleGetCollection={this.handleGetCollection}
          handleBannerClick={this.handleBannerClick}
          loading={loading}
          trackTappedLayoutBlock={this.props.trackTappedLayoutBlock}
          trackTappedLayout={this.props.trackTappedLayout}
        />
      </div>
    );
  }
}

HomeFeedResults.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  titleLink: PropTypes.string,
  results: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool.isRequired,
  showLocationSearchModal: PropTypes.func.isRequired,
  onGetCollection: PropTypes.func.isRequired,
  onExitCollection: PropTypes.func.isRequired,
  trackTappedFilterOption: PropTypes.func.isRequired,
  applyFilters: PropTypes.func.isRequired,
  searchId: PropTypes.string,
  trackViewedHome: PropTypes.func.isRequired,
  trackViewedCollection: PropTypes.func.isRequired,
  selectedFilters: PropTypes.arrayOf(PropTypes.string).isRequired,
  appliedFilters: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      target_params: PropTypes.shape({
        queryParams: PropTypes.string.isRequired,
      }),
    }),
  ).isRequired,
  clearFilter: PropTypes.func.isRequired,
  applyActionTarget: PropTypes.func.isRequired,
  error: PropTypes.shape({
    status: PropTypes.number.isRequired,
    message: PropTypes.string.isRequired,
    data: PropTypes.array,
  }),
  trackTappedLayout: PropTypes.func.isRequired,
  trackTappedLayoutBlock: PropTypes.func.isRequired,
  isRedirectToHomeActive: PropTypes.bool,
  homeFeedUrl: PropTypes.string,
  lastHomeFeed: PropTypes.string,
};

HomeFeedResults.defaultProps = {
  title: '',
  subtitle: '',
  titleLink: '',
  searchId: '',
  error: undefined,
  isRedirectToHomeActive: false,
  homeFeedUrl: undefined,
  lastHomeFeed: '',
};

const buildErrorBannerBlock = () => [
  {
    header: null,
    key: 'server-error-layout',
    blocks: [
      {
        key: 'server-error-block',
        header: i18n.gettext('home.generic_error.heading'),
        caption: i18n.gettext('home.generic_error.message'),
        buttonCaption: i18n.gettext('home.generic_error.btn'),
        typeName: 'UIBanner',
        theme: 'BANNER_SERVER_ERROR',
        target: { action: 'RETRY_GET_HOME_FEED', typeName: 'UITargetAction' },
      },
    ],
    typeName: 'UILayoutList',
  },
];

export const mapStateToProps = (state) => {
  const { feed, filters } = state.home;
  const loading = feed.loading;
  const title = getTitle(state);

  let results = feed.results.data;

  if (feed.error && !results.length) {
    results = buildErrorBannerBlock();
  }

  const homeFeedUrl = buildHomeFeedUrl(state);
  const lastHomeFeed = state.request.lastHomeFeed;

  return {
    results,
    subtitle: getSubtitle(state),
    loading,
    deliveryTime: 'ASAP',
    selectedCategories: filters.categoryFilters,
    selectedOffers: filters.offerFilters,
    title,
    titleLink: getCollectionLink(state) || '',
    appliedFilters: feed.appliedFilters,
    searchId: getSearchId(state),
    selectedFilters: buildSelectedFilters(state),
    error: feed.error,
    isRedirectToHomeActive: isRedirectToHomeActive(state),
    homeFeedUrl,
    lastHomeFeed,
  };
};

export default connect(
  mapStateToProps,
  (dispatch) => ({
    onGetCollection: (selectedFilters) =>
      dispatch(requestHomeFeed({ selectedFilters, clearCollection: true })),
    onExitCollection: () => dispatch(requestHomeFeed({ clearCollection: true })),
    showLocationSearchModal: () => dispatch(showLocationSearchModal()),
    trackViewedHome: (extraTrackingProps) => dispatch(trackViewedHome(extraTrackingProps)),
    trackViewedCollection: () => dispatch(trackViewedCollection()),
    clearFilter: (selectedFilters) => dispatch(requestHomeFeed({ selectedFilters })),
    applyFilters: (selectedFilters) => dispatch(requestHomeFeed({ selectedFilters })),
    trackTappedFilterOption: (options) => dispatch(trackTappedFilterOption(options)),
    applyActionTarget: (action) => dispatch(applyActionTarget(action)),
    trackTappedLayoutBlock: (trackingIds) => dispatch(trackTappedLayoutBlock(trackingIds)),
    trackTappedLayout: (trackingId) => dispatch(trackTappedLayout(trackingId)),
  }),
)(HomeFeedResults);
